import React, { useState, useEffect } from 'react';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { Upload, Trash2, Copy, Image as ImageIcon, Loader, RefreshCw } from 'lucide-react';

interface ImageItem {
    name: string;
    fullPath: string;
    url: string;
    ref: any;
}

interface ImageManagerProps {
    showToast?: (msg: string) => void;
}

const ImageManager: React.FC<ImageManagerProps> = ({ showToast }) => {
    const [images, setImages] = useState<ImageItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchImages = async () => {
        setLoading(true);
        try {
            const listRef = ref(storage, 'template-images');
            const res = await listAll(listRef);

            const imagePromises = res.items.map(async (itemRef) => {
                const url = await getDownloadURL(itemRef);
                return {
                    name: itemRef.name,
                    fullPath: itemRef.fullPath,
                    url: url,
                    ref: itemRef
                };
            });

            const imageList = await Promise.all(imagePromises);
            setImages(imageList);
        } catch (error) {
            console.error("Error fetching images:", error);
            if (showToast) showToast('Error fetching images. Ensure Storage is enabled.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchImages();
    }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            if (showToast) showToast('File size must be less than 2MB');
            return;
        }

        setUploading(true);
        try {
            const storageRef = ref(storage, `template-images/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            if (showToast) showToast('Image uploaded successfully');
            fetchImages();
        } catch (error) {
            console.error("Upload error:", error);
            if (showToast) showToast('Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (imageRef) => {
        if (!confirm("Are you sure you want to delete this image?")) return;
        try {
            await deleteObject(imageRef);
            if (showToast) showToast('Image deleted');
            setImages(prev => prev.filter(img => img.ref !== imageRef));
        } catch (error) {
            console.error("Delete error:", error);
            if (showToast) showToast('Delete failed.');
        }
    };

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url);
        if (showToast) showToast('URL copied to clipboard!');
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <ImageIcon className="text-indigo-500" /> Image Assets
                </h3>
                <div className="flex gap-2">
                    <button onClick={fetchImages} className="p-2 text-slate-500 hover:text-indigo-500 cursor-pointer">
                        <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                    </button>
                    <label className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors">
                        {uploading ? <Loader className="animate-spin" size={16} /> : <Upload size={16} />}
                        <span>Upload Image</span>
                        <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="hidden" />
                    </label>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader className="animate-spin text-indigo-500" size={32} />
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((img) => (
                        <div key={img.fullPath} className="group relative border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900">
                            <div className="aspect-square w-full overflow-hidden bg-white dark:bg-black/20 flex items-center justify-center">
                                <img src={img.url} alt={img.name} className="object-contain w-full h-full" />
                            </div>
                            <div className="p-3">
                                <p className="text-xs text-slate-500 truncate mb-2">{img.name}</p>
                                <div className="flex justify-between gap-2">
                                    <button onClick={() => copyToClipboard(img.url)} className="flex-1 flex items-center justify-center gap-1 text-xs bg-indigo-50 text-indigo-600 py-1.5 rounded cursor-pointer hover:bg-indigo-100">
                                        <Copy size={12} /> Copy URL
                                    </button>
                                    <button onClick={() => handleDelete(img.ref)} className="p-1.5 text-red-500 hover:bg-red-50 rounded cursor-pointer">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageManager;
